import { Control, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  LANGUAGES,
  LanguagesType,
  ProjectDetailsMode,
  ProjectFormValues,
  SupportType,
} from './consts';
import {
  Button,
  Input,
  MultiSelectDropdown,
  RichTextEditor,
} from '@platform/components';
import { DeliverablesList, FormBlock } from './utils';

const languageOptions = LANGUAGES.map((lang) => ({
  label: lang,
  value: lang,
}));


/* These specific headers and the overall organization of this page is kind a trainwreck. For this
 * version of the feature we're just looking for a minimally sensible organization given this is a
 * *mostly* straightforward port of the legacy app page, and we'll have to redesign the UX later. */
const ALL_PROJECTS_INFO_HEADER: Record<ProjectDetailsMode, string> = {
  'rep-create': 'General Information',
  'rep-edit': 'General Information',
  admin: 'Details (All project types)',
};

const SUPPORT_TYPE_DETAILS_INFO_HEADER: Record<ProjectDetailsMode, string> = {
  'rep-create': 'Final Details For Your {supportType} Request',
  'rep-edit': 'Final Details For Your {supportType} Request',
  admin: 'Details For {supportType} Requests',
};

const ProjectDetailsForm = ({
  mode,
  setValue,
  watch,
  register,
  control,
  onSubmit,
  supportTypes,
  isSubmitEnabled,
  isSubmitting,
}: {
  mode: ProjectDetailsMode;
  setValue: UseFormSetValue<ProjectFormValues>;
  watch: UseFormWatch<ProjectFormValues>;
  register: UseFormRegister<ProjectFormValues>;
  control: Control<ProjectFormValues>;
  onSubmit: () => void;
  supportTypes: Set<SupportType>;
  isSubmitEnabled: boolean;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
}) => {
  const getSupportTypeHeader = (supportType: SupportType) => {
    return SUPPORT_TYPE_DETAILS_INFO_HEADER[mode].replace('{supportType}', supportType);
  };

  return (
    <>
      <div className='w-2xl flex flex-col gap-4'>
        {/* General information, shows regardless of support type(s) selected */}
        <FormBlock header={ALL_PROJECTS_INFO_HEADER[mode]}>
          <MultiSelectDropdown
            register={() => {
              return register('languages');
            }}
            selectedItems={watch('languages') || []}
            onSelectionChange={(v) => setValue('languages', v as LanguagesType[])}
            options={languageOptions}
            placeholder='Select one or more languages'
            label='What languages would an experteer need to complete the project(s)?'
            filterable
            error={
              Array.from(watch('languages')).length === 0 ?
                'Must select at least one language'
              : undefined
            }
          />
        </FormBlock>
        {/* Trainer */}
        {supportTypes.has('Trainer') && (
          <FormBlock header={getSupportTypeHeader('Trainer')}>
            <p className='text-sm font-medium text-gray-500'>
              What training topic would help you and your team?
            </p>
            <DeliverablesList control={control} fieldKey='trainingDeliverables' />
          </FormBlock>
        )}
        {/* Call */}
        {supportTypes.has('Call') && (
          <FormBlock header={getSupportTypeHeader('Call')}>
            <p className='text-sm font-medium text-gray-500'>
              What are you hoping to get out of an expert advice session?
            </p>
            <DeliverablesList control={control} fieldKey='callOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Mentor */}
        {supportTypes.has('Mentor') && (
          <FormBlock header={getSupportTypeHeader('Mentor')}>
            <p className='text-sm font-medium text-gray-500'>
              What are you hoping to get out of a mentor?
            </p>
            <DeliverablesList control={control} fieldKey='mentorOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Coach */}
        {supportTypes.has('Coach') && (
          <FormBlock header={getSupportTypeHeader('Coach')}>
            <p className='text-sm font-medium text-gray-500'>
              What are you hoping to get out of a coach?
            </p>
            <DeliverablesList control={control} fieldKey='coachOutcomesDeliverables' />
          </FormBlock>
        )}
        {/* Consultant */}
        {supportTypes.has('Consultant') && (
          <FormBlock header={getSupportTypeHeader('Consultant')}>
            {/* This field is duplicate with Project name when we're on the edit page */}
            {mode === 'rep-create' && (
              <Input
                type='inline'
                label='Project Title'
                value={watch('projectTitle') || ''}
                placeholder='Please enter a project title'
                onChange={(e) => {
                  setValue('projectTitle', e.target.value);
                }}
              />
            )}
            <p className='text-sm font-medium text-gray-500'>
              What are the expected deliverables?
            </p>
            <DeliverablesList control={control} fieldKey='consultantDeliverables' />
            <RichTextEditor
              label=' Explain the background of the project and why your organization needs support'
              value={watch('projectBkgRichText') || ''}
              onChange={(value) => {
                setValue('projectBkgRichText', value.target.value);
                dataTestId = 'project-bkg-rte';
              }}
              dataTestId='project-bkg-rte'
            />
            <RichTextEditor
              label='What can the experteer learn by working with you? (Optional)' // TODO: Figure out how we should indicate optional vs required
              value={watch('learningOutcomesRichText') || ''}
              onChange={(value) => {
                setValue('learningOutcomesRichText', value.target.value);
              }}
              dataTestId='learning-outcomes-rte'
            />
          </FormBlock>
        )}
        <div className='w-fit mx-auto'>
          <Button
            variant='accent'
            size='lg'
            onPress={onSubmit}
            isDisabled={!isSubmitEnabled || isSubmitting}
          >
            {mode === 'rep-create' ? 'Submit for Review' : 'Submit'}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ProjectDetailsForm;
